import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { PriceCard, PropertyCard } from "@shared/cards";
import {
  PropertyListResponse,
  PropertySummaryResponse,
  StoryObject,
} from "@interfaces/properties.interface";

import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  useJsApiLoader,
  GoogleMap,
  Marker,
  Autocomplete,
  DirectionsRenderer,
} from "@react-google-maps/api";

import {
  getSimilarProperties,
  getSingleListingProperty,
  getSingleProperty,
} from "@services/krent.service";
import NeighbourStory from "@shared/cards/NeighbourStory";
import { Modal } from "@shared/controls/Modal";
import { Modal as NewModal } from "react-responsive-modal";
import GetMeta from "@pages/SeoPages/GetMeta";
import { ToastContainer } from "react-toastify";
import { useSessionStorage } from "../../hooks";
import PropertyImageGallery from "@shared/PropertyImageGallery";
import "../shared/cards/PropertyCard.css";
import { getAmenityIcon } from "../../utils/amenityIcons";
import RatingsAndReviews from "@shared/reviews/review-card";
import CancellationPolicy from "@shared/cancellation-policy";
import { BaseApiResponse, PaginatedResponse } from "@interfaces/app.interface";
import { scheduleScrollToTop, scrollPageToTop } from "@utils/scrollToTop";

type Props = {
  prop: PropertyListResponse;
  singleProperty?: PropertyListResponse;
};

export const SingleProperty: React.FC<Props> = ({
  prop,
  singleProperty,
}: Props) => {
  const location: any = useLocation();
  const [user] = useSessionStorage("user", {});

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env["REACT_APP_GOOGLE_API_KEY"] || "",
    libraries: ["places"],
  });
  const [fecthedSingleProp, setFecthedSingleProp] = useState<any>({});
  const [propertyInfo, setPropertyInfo] = useState<any>(prop);
  const [map, setMap] = useState<google.maps.Map>();
  const [directionsResponse, setDirectionsResponse] = useState<any>(null);
  const [distance, setDistance] = useState<any>("");
  const [duration, setDuration] = useState<any>("");
  const [propLatLng, setPropLatLng] = useState<any>("s");
  const [nStory, setNStory] = useState<any>();
  const [des, setDes] = useState<any>();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>("");
  const [tourLoading, setTourLoading] = useState<boolean>(false);
  const [openAmenities, setOpenAmenities] = useState<boolean>(false);
  const [openRules, setOpenRules] = useState<boolean>(false);
  const [openPolicy, setOpenPolicy] = useState<boolean>(false);
  const [center, setCenter] = useState<any>({
    lat: 9.082,
    lng: 8.6753,
  });
  const [similarProperties, setSimilarProperties] =
    useState<PropertyListResponse[]>();
  const pageTopRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.body.style.overflow = "auto";
    document.body.style.pointerEvents = "auto";
  }, []);

  useLayoutEffect(() => {
    return scheduleScrollToTop(pageTopRef.current);
  }, [location.pathname, location.key]);

  const navigate = useNavigate();
  const originRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const abortController = new AbortController();
    const propertyId = location?.state?.property?.id || singleProperty?.id;

    if (propertyId) {
      getSimilarProperties(propertyId, 5, 4, abortController.signal)
        .then(
          (
            res: BaseApiResponse<
              PropertyListResponse[],
              PaginatedResponse,
              PropertySummaryResponse
            >,
          ) => {
            setSimilarProperties(res.data);
          },
        )
        .catch(() => {});
    }

    return () => abortController.abort();
  }, [location?.state?.property?.id, singleProperty?.id]);
  useEffect(() => {
    const abortController = new AbortController();

    const fetchData = async () => {
      try {
        let res;

        if (location?.state?.property?.id) {
          const id = location.state.property.id;
          res = await getSingleListingProperty(id, abortController.signal);
        } else {
          const pathname = decodeURIComponent(location.pathname ?? "");
          const slug = pathname.match(/\/property\/(.+)$/)?.[1];
          if (!slug) {
            console.error("No slug found in pathname:", pathname);
            return;
          }
          res = await getSingleProperty(slug, abortController.signal);
        }

        const data = (res as any)?.data;

        if (!data) return;

        setFecthedSingleProp(data);
        setPropertyInfo(data);
        scrollPageToTop(pageTopRef.current);

        if (data?.neighbour_story) {
          setNStory(Object.entries(data.neighbour_story));
        }

        if (data?.location?.coordinates) {
          setCenter({
            lat: data.location.coordinates[1],
            lng: data.location.coordinates[0],
          });
          setPropLatLng(
            `${data.location.coordinates[1]}, ${data.location.coordinates[0]}`,
          );
        }
      } catch (err: any) {
        console.error(err?.errors?.[0]?.message || err);
      }
    };

    fetchData();

    return () => abortController.abort();
  }, [location?.state?.property?.id, location?.state?.id, location.pathname]);

  async function calculateRoute() {
    if (originRef.current!.value === "") {
      return;
    }
    // eslint-disable-next-line no-undef
    const directionsService = new google.maps.DirectionsService();
    const results = await directionsService.route({
      origin: originRef.current!.value,
      destination: propLatLng,
      // eslint-disable-next-line no-undef
      travelMode: google.maps.TravelMode.DRIVING,
    });

    setDirectionsResponse(results);
    setDistance(results.routes[0].legs[0].distance!.text);
    setDuration(results.routes[0].legs[0].duration!.text);
  }
  async function calculateStory() {
    // eslint-disable-next-line no-undef
    const directionsService = new google.maps.DirectionsService();
    const results = await directionsService.route({
      origin: des,
      destination: propLatLng,
      travelMode: google.maps.TravelMode.DRIVING,
    });

    setDirectionsResponse(results);
    setDistance(results.routes[0].legs[0].distance!.text);
    setDuration(results.routes[0].legs[0].duration!.text);
  }
  function clearRoute() {
    setDirectionsResponse(null);
    setDistance("");
    setDuration("");
    setDes("");
    originRef.current!.value = "";
  }

  function openGoogleMapsDirections() {
    const originAddress = originRef.current?.value?.trim() || des?.trim();
    let url: string;

    if (originAddress) {
      url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(originAddress)}&destination=${encodeURIComponent(propLatLng)}`;
    } else if (center?.lat && center?.lng) {
      url = `https://www.google.com/maps/dir/?api=1&destination=${center.lat},${center.lng}`;
    } else {
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  }
  if (!propertyInfo || !fecthedSingleProp) {
    return (
      <>
        <div
          ref={pageTopRef}
          id="property-page-top"
          aria-hidden="true"
          style={{ scrollMarginTop: "7rem" }}
        />
        <h4>Loading...</h4>
      </>
    );
  }
  const sellerAccount = ["seller", "agent", "host", "landlord", "property-manager"].includes(user?.role);
  const listingOwnerId = typeof propertyInfo.user === "string" ? propertyInfo.user : propertyInfo.user?.id || propertyInfo.user?._id;
  const ownsListing = !!listingOwnerId && [user?.id, user?._id].includes(listingOwnerId);
  if (sellerAccount && !ownsListing && ["long lease", "shortlet"].includes(propertyInfo.intent)) {
    return <div className="container py-5"><h4>Property unavailable for this account</h4><p>Use a renter account to browse rent and shortlet properties.</p><Link to="/">Back to home</Link></div>;
  }
  return (
    <div className="container-lg container-fluid-md pt-2">
      <div
        ref={pageTopRef}
        id="property-page-top"
        aria-hidden="true"
        style={{ scrollMarginTop: "7rem" }}
      />
      <ToastContainer limit={1} />

      <span
        className="mb-3 d-inline-flex align-items-center gap-2 cursor-pointer font-onest"
        style={{
          position: "relative",
          zIndex: 5,
          paddingTop: "0.25rem",
        }}
        onClick={() => {
          navigate(-1);
        }}
      >
        <i className="fa fa-arrow-left"></i>
        <span>Back to property</span>
      </span>
      <GetMeta
        description={propertyInfo?.description}
        title={`${propertyInfo?.title} (Ref:${propertyInfo?.referenceNumber}) | Krent`}
        canonicalValue={location?.pathname}
      />
      <div className="col pb-4">
        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-1 gap-md-3">
          <h1 className="fw-bolder fs-24 font-onest mb-0">
            {propertyInfo?.title}
          </h1>

          <div className="d-flex flex-wrap gap-2 align-items-center">
            {propertyInfo?.instantBooking && (
              <span
                className="badge rounded-pill px-2 py-2 fs-12 fs-md-14 fw-500 font-onest instant-booking-badge position-relative"
                data-tooltip="Confirmed instantly"
                style={{ background: "#47BBA90D", color: "#0A0F1E" }}
              >
                <span className="bg-primary p-1 rounded-circle d-inline-flex align-items-center justify-content-center">
                  <i
                    className="fa-regular text-white fa-house"
                    style={{ fontSize: "10px" }}
                  ></i>
                </span>{" "}
                &nbsp; Instant Property
              </span>
            )}

            {propertyInfo?.isTopListing && (
              <span
                className="badge rounded-pill bg-primary px-2 px-md-3 py-2 fw-semibold fs-12 fs-md-14"
                style={{ boxShadow: "0 2px 6px rgba(0,0,0,0.25)" }}
              >
                {propertyInfo?.topListingLabel || "★ Top Listing"}
              </span>
            )}
          </div>
        </div>

        <div className="d-flex items-center justify-items-center gap-4 mt-2 mt-md-0">
          <p className="fs-14 m-0 font-onest">
            {propertyInfo?.location2?.city || propertyInfo?.location?.city}
          </p>

          {propertyInfo?.referenceNumber && (
            <p className="fs-14 m-0 font-onest">
              Ref : {propertyInfo.referenceNumber}
            </p>
          )}
        </div>
      </div>

      <PropertyImageGallery
        coverImage={propertyInfo?.coverImage}
        otherImages={propertyInfo?.otherImages}
        videoUrl={propertyInfo?.video_url}
        title={propertyInfo?.title}
        slug={propertyInfo?.slug}
      />
      {propertyInfo?.virtual_tour_url && (
        <div className="col-12">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#e8f5f3",
              borderRadius: "12px",
              padding: "16px 20px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <div
                style={{
                  backgroundColor: "#3dab93",
                  borderRadius: "10px",
                  width: "48px",
                  height: "48px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <i
                  className="fa-regular fa-home"
                  style={{ color: "#fff", fontSize: "20px" }}
                />
              </div>
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "15px",
                    color: "#1a1a1a",
                  }}
                >
                  Tour this property from anywhere.
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#5a7a74",
                    marginTop: "2px",
                  }}
                >
                  Explore this property in immersive 3D virtual tour
                </div>
              </div>
            </div>

            {/* Right: CTA Button */}
            <button
              onClick={() => {
                setModalType("tour");
                setTourLoading(true);
                setShowModal(true);
              }}
              style={{
                backgroundColor: "#3dab93",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                padding: "10px 20px",
                fontWeight: 600,
                fontSize: "14px",
                cursor: "pointer",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
              className="font-onest"
            >
              Launch tour
            </button>
          </div>
        </div>
      )}

      <div className="row py-3">
        <div className="col-12 col-md-8">
          <div className="col-12 col-md-4 d-md-none">
            {nStory?.length > 0 && (
              <div className="border-1 rounded border-purple ">
                <div className="d-flex flex-column p-4 pb-0">
                  <h5 className="fw-bold mb-2">Neighbourhood</h5>
                  <p className="mb-0">Landmarks around this property</p>
                  <span className="mb-0 fs-12 text-primary">
                    Double click on any card to see the distance and duration to
                    this property on the map.
                  </span>
                  <hr />
                </div>
                <div
                  className="d-flex flex-column p-4 pt-0"
                  style={{ maxHeight: "40rem", overflow: "scroll " }}
                >
                  {nStory?.map(
                    (item: [string, StoryObject[]], index: number) => {
                      return (
                        <NeighbourStory
                          item={item}
                          key={index + "a"}
                          setDes={setDes}
                          calculateStory={calculateStory}
                          originRef={originRef}
                        />
                      );
                    },
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="d-flex flex-column rounded ">
            <h5 className=" fs-18 mb-3 font-onest">
              Property details
              {/* Entire {propertyInfo?.kind?.title.toLowerCase()} hosted by{" "}
              {`${propertyInfo?.user?.firstname} ${propertyInfo?.user?.lastname}`}{" "} */}
              {/* {propertyInfo?.affiliation || "owner"} */}
            </h5>
            <div className="d-flex flex-wrap align-items-center gap-3 text-muted">
              <div className="d-flex align-items-center gap-2">
                <i className="fa-regular fa-users text-primary"></i>
                <span className="fw-medium">{propertyInfo?.guests} Guests</span>
              </div>

              <div className="vr d-none d-md-block"></div>

              <div className="d-flex align-items-center gap-2">
                <i className="fa-light fa-bed text-primary"></i>
                <span className="fw-medium">
                  {propertyInfo?.bedrooms} Bedrooms
                </span>
              </div>

              <div className="vr d-none d-md-block"></div>

              <div className="d-flex align-items-center gap-2">
                <i className="fa-regular fa-bath text-primary"></i>
                <span className="fw-medium">
                  {propertyInfo?.bathrooms} Bathrooms
                </span>
              </div>

              {propertyInfo?.flood_meter && (
                <>
                  <div className="vr d-none d-md-block"></div>

                  <div className="d-flex align-items-center gap-2 text-capitalize">
                    <i className="fa-solid fa-thermometer-half text-primary"></i>
                    <span className="fw-medium">
                      {propertyInfo?.flood_meter} Flood Meter
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
          <hr />

          <div>
            {propertyInfo?.amenities.length > 0 && (
              <>
                <h4 className="fs-18 fw-semibold mb-1 font-onest">
                  What this place offers
                </h4>
                <p
                  className="fs-14 font-onest mb-2"
                  style={{ overflowWrap: "break-word" }}
                >
                  {propertyInfo?.description
                    ? propertyInfo.description.charAt(0).toUpperCase() +
                      propertyInfo.description.slice(1)
                    : ""}
                </p>
                <div className="row mt-2">
                  {propertyInfo?.amenities
                    ?.map((item: any, i: number) => {
                      return (
                        <div className="col-12 mb-2" key={i}>
                          {/* <div
                            className="font-onest fw-medium mb-2"
                            style={{ color: "#0A0F1E" }}
                          >
                            {item.title}
                          </div> */}
                          {Array.isArray(item.offers) && (
                            <div className="row mt-2">
                              {item.offers.map((offer: any, index: number) => {
                                const offerText =
                                  typeof offer === "string"
                                    ? offer
                                    : typeof offer === "object"
                                      ? JSON.stringify(offer)
                                      : String(offer);

                                const icon = getAmenityIcon(offerText);

                                return (
                                  <div
                                    className="col-6 mb-3"
                                    key={`${i}-${index}`}
                                  >
                                    <div className="d-flex align-items-center gap-2">
                                      <i
                                        className={`${icon} text-black p-2 rounded-2 fs-16`}
                                        style={{
                                          minWidth: "20px",
                                          background: "#F3F4F6",
                                        }}
                                      ></i>
                                      <div className="flex-grow-1">
                                        <div
                                          className="font-onest fw-medium"
                                          style={{ color: "#0A0F1E" }}
                                        >
                                          {offerText}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })
                    .slice(
                      0,
                      window.innerWidth <= 400
                        ? 1
                        : window.innerWidth > 400 && window.innerWidth <= 768
                          ? 2
                          : 3,
                    )}
                  {propertyInfo.amenities?.reduce(
                    (total: number, item: any) => {
                      return (
                        total +
                        (Array.isArray(item.offers) ? item.offers.length : 0)
                      );
                    },
                    0,
                  ) > 4 && (
                    <div
                      className="text-link fs-12 d-flex align-items-center text-black border rounded-3 w-max p-2 ml-2 fw-500"
                      onClick={() => setOpenAmenities(true)}
                    >
                      Show all Amenities
                    </div>
                  )}
                </div>
              </>
            )}
            {/* <Calendar /> */}
          </div>
          <hr />

          <div className="col-12 d-md-none mt-4">
            <PriceCard
              id={propertyInfo?.id}
              type={propertyInfo?.intent}
              price={propertyInfo?.price}
              duration={propertyInfo?.cycle}
              currency={propertyInfo?.currency}
              cautionFee={propertyInfo?.cautionFee}
              partyCautionFee={propertyInfo?.partyCautionFee}
              owner={propertyInfo?.user?.id || propertyInfo?.user?._id || ""}
              ownerName={propertyInfo?.user?.preferredName}
              virtualTour={propertyInfo?.virtualTour}
              status={propertyInfo?.availability_status}
              role={user.role}
              partyPrice={propertyInfo?.partyPrice}
              propertyInfo={propertyInfo}
            />
          </div>

          <RatingsAndReviews
            propertyId={propertyInfo?.id || propertyInfo?._id || ""}
          />

          <hr className="hr mt-4" />
          <div className="d-flex flex-column mb-5">
            {isLoaded && (
              <>
                <h3 className="fs-16 fw-500 my-1 font-onest">
                  Where you will be (Map)
                </h3>

                <div className="md-flex gap-2">
                  <form className="mt-4 mt-md-3">
                    <div className="">
                      <div className="mb-3">
                        <label
                          className="text-uppercase fw-600 fs-12"
                          htmlFor="origin"
                        >
                          Find the distance from
                        </label>
                        <div className="input-group mt-1">
                          <span
                            className="input-group-text input-group-text-0"
                            id="origin-addon"
                          >
                            <i className={`iconly-Location icli fs-4`}></i>
                          </span>
                          <Autocomplete className="form-control border-start-0">
                            <input
                              type="text"
                              className="form-control form-control-0 ps-3 border-0 py-2 border-start-0"
                              id="origin"
                              placeholder="Enter your address"
                              ref={originRef}
                              required
                              style={{ borderRight: "none" }}
                            />
                          </Autocomplete>
                        </div>
                      </div>

                      <div className="form-group mb-3">
                        <div className="d-flex justify-between align-items-center">
                          <label
                            className="text-uppercase fw-600 fs-14 m-1"
                            htmlFor="property"
                          >
                            to?
                          </label>
                          <div className="d-flex align-items-center">
                            <span className="input-group-text input-group-text-0 border-0 p-0">
                              <i
                                className={`iconly-Location icli fs-4 text-primary`}
                              ></i>
                            </span>
                            <span className="m-0 text-uppercase fw-600 text-primary">
                              This Property
                            </span>
                          </div>
                        </div>
                        <div className="d-flex flex-column flex-md-row gap-2 gap-md-3 mt-4 w-100 align-items-stretch">
                          <button
                            type="button"
                            className="btn border-primary text-primary map-btn fs-12 fw-600 px-4 w-100 flex-md-fill"
                            onClick={clearRoute}
                          >
                            Clear route
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary map-btn fs-12 fw-600 px-4 w-100 flex-md-fill"
                            onClick={() => {
                              calculateRoute();
                            }}
                          >
                            Get route
                          </button>
                          <button
                            type="button"
                            className="btn border-primary text-primary map-btn fs-12 fw-600 px-4 w-100 flex-md-fill d-inline-flex align-items-center justify-content-center gap-2"
                            onClick={openGoogleMapsDirections}
                          >
                            <i className="fa-solid fa-diamond-turn-right"></i>
                            Open in Google Maps
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                  <div className="d-flex flex-column flex-md-row gap-2 align-items-center mb-3 justify-content-between">
                    <div className="row gap-3 w-100">
                      <p className="fw-bold m-0">
                        Distance:{" "}
                        <span className="text-danger">{distance}</span>
                      </p>
                      <p className="fw-bold m-0">
                        Duration:{" "}
                        <span className="text-danger">{duration}</span>
                      </p>
                    </div>
                  </div>
                </div>

                {center && (
                  <div style={{ height: "25rem", position: "relative" }}>
                    <GoogleMap
                      center={center}
                      zoom={15}
                      mapContainerStyle={{ width: "100%", height: "100%" }}
                      onLoad={(map) => {
                        setMap(map);
                      }}
                    >
                      <Marker
                        position={center}
                        onClick={openGoogleMapsDirections}
                        title="Get directions in Google Maps"
                      />

                      {directionsResponse && (
                        <DirectionsRenderer directions={directionsResponse} />
                      )}
                    </GoogleMap>

                    <div
                      className="d-flex flex-column gap-2"
                      style={{
                        position: "absolute",
                        left: "30px",
                        bottom: "30px",
                      }}
                    >
                      <div
                        className="bg-white rounded-circle d-flex align-items-center justify-content-center social-icon cursor-pointer"
                        title="Get directions in Google Maps"
                        onClick={openGoogleMapsDirections}
                      >
                        <i className="fa-solid fa-diamond-turn-right fs-22 text-primary"></i>
                      </div>
                      <div
                        className="bg-white rounded-circle d-flex align-items-center justify-content-center social-icon cursor-pointer"
                        title="Recenter map"
                        onClick={() => {
                          map!.panTo(center);
                          map!.setZoom(15);
                        }}
                      >
                        <i className="fa fa-location-arrow fs-22 text-primary"></i>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            <hr className="mt-4 hr" style={{ border: "1px solid #000" }} />
          </div>
        </div>
        <div className="col-12 col-md-4 d-none d-md-block">
          {
            <PriceCard
              id={propertyInfo?.id}
              type={propertyInfo?.intent}
              price={propertyInfo?.price}
              duration={propertyInfo?.cycle}
              currency={propertyInfo?.currency}
              cautionFee={propertyInfo?.cautionFee}
              partyCautionFee={propertyInfo?.partyCautionFee}
              owner={propertyInfo?.user?.id || propertyInfo?.user?._id || ""}
              ownerName={propertyInfo?.user?.preferredName}
              virtualTour={
                propertyInfo?.virtual_tour_url && propertyInfo?.virtual_tour_url
              }
              status={propertyInfo?.availability_status}
              role={user.role}
              partyPrice={propertyInfo?.partyPrice}
              propertyInfo={propertyInfo}
            />
          }

          {nStory?.length > 0 && (
            <div className="border-1 rounded border-purple ">
              <div className="d-flex flex-column p-4 pb-0">
                <h5 className="fw-bold mb-2">Neighbourhood</h5>
                <p className="mb-0">Landmarks around this property</p>
                <span className="mb-0 fs-12 text-primary">
                  Double click on any card to see the distance and duration to
                  this property on the map.
                </span>
                <hr />
              </div>
              <div
                className="d-flex flex-column p-4 pt-0"
                style={{ overflow: "scroll " }}
              >
                {nStory?.map((item: [string, StoryObject[]], index: number) => {
                  return (
                    <NeighbourStory
                      item={item}
                      key={index + "a"}
                      setDes={setDes}
                      calculateStory={calculateStory}
                      originRef={originRef}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      {similarProperties && similarProperties.length > 0 && (
        <section className="">
          <h6 className="fw-bold mb-2">Similar listings</h6>
          <div className="row">
            {similarProperties?.slice(0, 3).map((v, k) => (
              <div className="col-12 col-md-4 mb-4 mb-md-0" key={k}>
                <PropertyCard {...v} />
              </div>
            ))}
          </div>
        </section>
      )}
      <Link
        to={"/explore/"}
        className="text-link fw-bold text-primary my-4 d-flex align-items-center"
      >
        Explore more listings <i className=" iconly iconly-Arrow-Right-2"></i>
      </Link>
      {/* Terms and Condition Modal */}
      <Modal
        show={showModal && modalType !== "tour"}
        onClose={() => setShowModal(false)}
        dismissible={false}
      >
        <div className="container py-3">
          <p className="fw-bold ">House Rules</p>
          <p>{propertyInfo?.rules && propertyInfo?.rules}</p>

          <button
            data-bs-dismiss="modal"
            className="btn btn-primary rounded-pill px-5 py-3 text-white fw-bold mt-3"
          >
            I understand
          </button>
        </div>
      </Modal>
      {/* Amenities Modal */}
      <NewModal
        open={openAmenities}
        center
        onClose={() => setOpenAmenities(false)}
        classNames={{
          modalAnimationIn: "customEnterModalAnimation",
          modalAnimationOut: "customLeaveModalAnimation",
          modal: "mt-7 mt-md-0",
        }}
        animationDuration={800}
      >
        <div className="p-3">
          {propertyInfo?.amenities.length > 0 && (
            <>
              <h4 className="fs-5 fw-bold mb-4">All Amenities</h4>
              <div className="row">
                {propertyInfo?.amenities?.map((item: any, i: number) => {
                  return (
                    <div className="col-12 mb-4" key={i}>
                      <div
                        className="font-onest fw-medium mb-2"
                        style={{ color: "#0A0F1E" }}
                      >
                        {item.title}
                      </div>
                      {Array.isArray(item.offers) && (
                        <div className="row">
                          {item.offers.map((offer: any, index: number) => {
                            const offerText =
                              typeof offer === "string"
                                ? offer
                                : typeof offer === "object"
                                  ? JSON.stringify(offer)
                                  : String(offer);

                            const icon = getAmenityIcon(offerText);

                            return (
                              <div className="col-6 mb-3" key={`${i}-${index}`}>
                                <div className="d-flex align-items-center gap-2">
                                  <i
                                    className={`${icon} text-black p-2 rounded-2 fs-16`}
                                    style={{
                                      minWidth: "20px",
                                      background: "#F3F4F6",
                                    }}
                                  ></i>
                                  <div className="flex-grow-1">
                                    <div
                                      className="font-onest fw-medium"
                                      style={{ color: "#0A0F1E" }}
                                    >
                                      {offerText}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </NewModal>
      {/* Policy Modal */}
      <NewModal
        open={openPolicy}
        center
        onClose={() => setOpenPolicy(false)}
        // classNames={{ modal: 'w-300 rounded' }}
        classNames={{
          // overlayAnimationIn: 'customEnterOverlayAnimation',
          // overlayAnimationOut: 'customLeaveOverlayAnimation',
          modalAnimationIn: "customEnterModalAnimation",
          modalAnimationOut: "customLeaveModalAnimation",
        }}
        animationDuration={800}
      >
        <div className="py-5 px-2">
          <>
            <div className="row">
              <h4 className="fs-5 fw-bold mt-1">Krent Cancellation Policy</h4>
              <hr />
              <div>
                {propertyInfo.cancellation_policy ? (
                  <p>{propertyInfo.cancellation_policy}</p>
                ) : (
                  <CancellationPolicy />
                )}
              </div>
            </div>
          </>
        </div>
      </NewModal>
      {/* House Rules Modal */}
      <NewModal
        open={openRules}
        center
        onClose={() => setOpenRules(false)}
        // classNames={{ modal: 'w-300 rounded' }}
        classNames={{
          // overlayAnimationIn: 'customEnterOverlayAnimation',
          // overlayAnimationOut: 'customLeaveOverlayAnimation',
          modalAnimationIn: "customEnterModalAnimation",
          modalAnimationOut: "customLeaveModalAnimation",
        }}
        animationDuration={800}
      >
        <div className="pt-3 px-2">
          <div className="row">
            <h4 className="fs-5 fw-bold my-3">House Rules</h4>
            <hr />
            <p className="light-text">{propertyInfo?.rules}</p>
          </div>
        </div>
      </NewModal>
      {modalType === "tour" && (
        <Modal
          key="tour-modal"
          show={showModal}
          onClose={() => {
            setShowModal(false);
            setTimeout(() => {
              setModalType("");
            }, 500);
          }}
          size={900}
          position="top"
          dismissible={true}
        >
          <div style={{ width: "100%", height: "80vh", position: "relative" }}>
            {tourLoading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f8f9fa",
                  zIndex: 10,
                }}
              >
                <div
                  className="spinner-border"
                  role="status"
                  style={{ width: "3rem", height: "3rem" }}
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
            <iframe
              title="Virtual Tour"
              src={propertyInfo?.virtual_tour_url}
              style={{ width: "100%", height: "100%", border: "none" }}
              allowFullScreen
              onLoad={() => setTourLoading(false)}
            ></iframe>
          </div>
        </Modal>
      )}
    </div>
  );
};
